import React, { useState } from 'react';
import { Calendar, MapPin } from 'lucide-react';
import { experience, projects, skills } from '../data/mock';

const ZahaEngineering = () => {
  const [activeTab, setActiveTab] = useState('experience');

  return (
    <section id="work" className="relative py-32 bg-white overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto px-8">
        {/* Header */}
        <div className="mb-20">
          <div className="inline-block mb-6">
            <p className="text-xs font-medium text-[#a0aec0] uppercase tracking-[0.25em]">
              Professional Work
            </p>
            <div className="w-16 h-[1px] bg-gradient-to-r from-[#cbd5e0] to-transparent mt-4" />
          </div>
          
          <h2 className="text-6xl md:text-7xl font-light text-[#2d3748] mb-4">
            Engineering
            <br />
            <span className="font-medium">Portfolio</span>
          </h2>
        </div>

        {/* Tabs - Flowing */}
        <div className="flex flex-wrap gap-3 mb-16">
          {[
            { id: 'experience', label: 'Experience' },
            { id: 'projects', label: 'Projects' },
            { id: 'skills', label: 'Expertise' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-8 py-4 text-sm font-medium tracking-wide transition-all ${
                activeTab === tab.id
                  ? 'bg-[#2d3748] text-white'
                  : 'bg-[#f7fafc] text-[#718096] hover:bg-[#edf2f7]'
              }`}
              style={{ borderRadius: '40px 20px 40px 20px' }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="max-w-6xl mx-auto">
          {activeTab === 'experience' && (
            <div className="space-y-8">
              {experience.map((exp) => (
                <div 
                  key={exp.id}
                  className="group relative bg-gradient-to-br from-[#f7fafc] to-white p-10 zaha-shadow hover:zaha-shadow-lg transition-all"
                  style={{ borderRadius: '50px 30px 50px 30px' }}
                >
                  <div className="absolute left-0 top-10 w-[2px] h-[calc(100%-80px)] bg-gradient-to-b from-[#cbd5e0] to-transparent" />
                  
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-6">
                    <div>
                      <h3 className="text-2xl font-medium text-[#2d3748] mb-3">
                        {exp.role}
                      </h3>
                      <p className="text-base font-medium text-[#4a5568] mb-2">{exp.company}</p>
                      <p className="text-sm text-[#a0aec0] flex items-center gap-2">
                        <MapPin size={14} />
                        {exp.location}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 bg-white px-5 py-3 zaha-shadow text-sm text-[#718096] font-medium"
                      style={{ borderRadius: '30px 20px 30px 20px' }}
                    >
                      <Calendar size={14} />
                      {exp.period}
                    </div>
                  </div>

                  <p className="text-[#718096] leading-relaxed mb-8">{exp.description}</p>

                  <div className="grid md:grid-cols-2 gap-4">
                    {exp.achievements.map((achievement, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-4 bg-white/50 hover:bg-white transition-colors"
                        style={{ borderRadius: '20px 10px 20px 10px' }}
                      >
                        <div className="w-1 h-6 bg-gradient-to-b from-[#cbd5e0] to-transparent flex-shrink-0" />
                        <p className="text-sm text-[#718096]">{achievement}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'projects' && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project) => (
                <div
                  key={project.id}
                  className="group bg-gradient-to-br from-[#f7fafc] to-white p-8 zaha-shadow hover:zaha-shadow-lg transition-all h-full flex flex-col"
                  style={{ borderRadius: '40px 30px 40px 30px' }}
                >
                  <div className="mb-4">
                    <div className="inline-block px-4 py-2 bg-white mb-4"
                      style={{ borderRadius: '20px 15px 20px 15px' }}
                    >
                      <span className="text-xs font-medium text-[#718096]">{project.year}</span>
                    </div>
                  </div>
                  
                  <h3 className="text-xl font-medium text-[#2d3748] mb-3 group-hover:text-[#4a5568] transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-sm font-medium text-[#718096] mb-2">{project.company}</p>
                  <p className="text-xs text-[#a0aec0] mb-4 flex items-center gap-1">
                    <MapPin size={12} />
                    {project.location}
                  </p>
                  <p className="text-sm text-[#718096] leading-relaxed mb-4 flex-grow">{project.description}</p>
                  
                  <div className="pt-4 border-t border-[#edf2f7]">
                    <p className="text-xs font-medium text-[#4a5568]">{project.scope}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'skills' && (
            <div className="grid md:grid-cols-2 gap-8">
              {Object.entries({
                'Estimating': skills.estimating,
                'Systems': skills.systems,
                'Software': skills.software,
                'Professional': skills.professional
              }).map(([category, items]) => (
                <div key={category} className="bg-gradient-to-br from-[#f7fafc] to-white p-10 zaha-shadow"
                  style={{ borderRadius: '50px 30px 50px 30px' }}
                >
                  <h3 className="text-2xl font-medium text-[#2d3748] mb-8">{category}</h3>
                  
                  <div className="space-y-3">
                    {items.map((skill, idx) => (
                      <div key={idx} className="flex items-center gap-4 group">
                        <div className="w-1 h-6 bg-gradient-to-b from-[#cbd5e0] to-transparent group-hover:h-8 transition-all" />
                        <p className="text-sm text-[#718096] group-hover:text-[#4a5568] transition-colors">{skill}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ZahaEngineering;